/**
 * The INBOUND half of "the app may not assert a session it does not have"
 * (issue #396). Its outbound twin is `lib/signOut.js` (#326).
 *
 * THE DEFECT. There was no global 401 handling on the shared axios instance.
 * If a session ended server-side while a tab was open — the 24h expiry
 * elapsing, an admin password reset revoking every session (#226), the user
 * row going away — the SPA kept rendering as signed in. `AuthContext` still
 * held the user, the nav and every gated route stayed exactly as they were,
 * and each call 401'd on its own into whatever `catch` its own component
 * happened to have. The screen said "you are signed in as Aarav" over a
 * session the server had already destroyed, indefinitely, until something
 * happened to force a reload.
 *
 * `ProtectedRoute` already *documents* the opposite — "that reset revokes
 * their sessions too (#226), so the next request is a 401 and the app
 * bounces to /login". Nothing bounced. This module is what makes that
 * sentence true.
 *
 * WHY A 401 IS SAFE TO ACT ON, AND NOTHING ELSE IS. `api/src/auth/middleware.ts`'s
 * `requireUser` answers 401 on exactly five paths — no token, no session
 * row, session expired, user gone, user row deleted mid-request — and it has
 * no `try`/`catch` around its D1 reads, so a database failure surfaces as an
 * uncaught 500, never as a 401. A 401 from this API therefore means "no live
 * session", full stop; it never means "the server had a bad moment". That is
 * the same property `sessionAlreadyEnded` in `signOut.js` already leans on,
 * and the reason this can be a hard rule rather than a heuristic.
 *
 * A 403 is emphatically NOT in scope. `PASSWORD_CHANGE_REQUIRED` (#295) is a
 * 403 carrying `code: "must_change_password"`, and it is issued for a session
 * that is fully alive and authenticated — the middleware's own comment says
 * it is a 403 *precisely so* the client does not treat it as anonymous and
 * bounce to /login, "which is a loop (the login succeeds, and lands right
 * back here)". Sweeping 403 in here would rebuild that loop from the other
 * end. The status check below is `=== 401`, and the test suite pins 403 as a
 * non-event.
 */

/** Where an ended session lands. A fixed, app-relative literal — see
 * `endSessionAndSignIn` for why nothing from the request or the current URL
 * is ever interpolated into it. */
export const SIGN_IN_PATH = "/login";
/**
 * Issue #874, slice S1. Two sibling literals, added here (not redeclared).
 *
 * NOT a detector workaround: `flagSafeLinks.test.js` (#800) flags an
 * imported constant's use exactly as it flags a literal — the ALLOWLIST
 * entry is what makes either one pass, not the spelling, and a single,
 * isolated call site gets no benefit from a constant it doesn't otherwise
 * need (App.js's catch-all redirect and SignUpNew.jsx's one "Sign in" link
 * both stayed plain literals, each with its own allowlisted reason — see
 * that file). These two constants exist because `FrontDoorNew.jsx` links
 * to sign-in TWICE and `SignInNew.jsx` links home TWICE — real reuse
 * within one screen, the same reason `SIGN_IN_PATH` itself already existed
 * before this issue, and the same shape `lib/coachSetup.js`'s
 * `COACH_SETUP_PATH` and `lib/homePath.js`'s own constants already use.
 */
export const FRONT_DOOR_PATH = "/";
export const SIGN_UP_PATH = "/register";
export const SESSION_ENDED_PARAM = "session";
export const SESSION_ENDED_VALUE = "expired";
export const SESSION_ENDED_DESTINATION = `${SIGN_IN_PATH}?${SESSION_ENDED_PARAM}=${SESSION_ENDED_VALUE}`;

/**
 * What the sign-in page says when it was reached this way.
 *
 * Says the session ended and nothing else. It deliberately does not guess
 * WHY — expiry, an admin reset, and a deleted account are indistinguishable
 * from the client, and a specific-sounding wrong reason ("your session timed
 * out") is worse than a general right one for the person whose account was
 * just reset by an administrator.
 */
export const SESSION_ENDED_MESSAGE =
  "Your session ended, so we signed you out. Sign in again to pick up where you left off.";

/**
 * Requests whose 401 is NOT about a live session dying. Written as paths
 * relative to the API root (`/api`), which is what `requestApiPath` below
 * normalises every request down to.
 *
 * Each entry earns its place — an over-broad exemption here is a silent hole
 * in the fix, and a missing one is a redirect loop:
 *
 *   - `/auth/login` — a wrong password IS a 401 (`INVALID_CREDENTIALS`, and
 *     deliberately the same body as an unknown email, so login cannot be used
 *     to enumerate users). Acting on it would turn every typo into a reload
 *     back onto the sign-in page with "your session ended" over the top of
 *     it, destroying the form state and the real error message with it.
 *   - `/auth/change-password` — the same 401 for the same reason: the route
 *     verifies `current_password` against the stored hash and answers
 *     `INVALID_CREDENTIALS` when it does not match. That screen
 *     (`ForcePasswordChange`) is one a confined user cannot leave, and its
 *     whole design is to show the server's specific reason inline. Bouncing
 *     them to /login mid-change would send them back through a sign-in that
 *     lands them on exactly the same screen.
 *   - `/auth/me` — a 401 here is the ordinary ANONYMOUS answer, which every
 *     visitor to the public landing page gets on first paint. `AuthContext`
 *     already owns it (`catch { setUser(false) }`) and `ProtectedRoute`
 *     already routes on it. Not exempting this one would send every
 *     signed-out visitor of `/` to "your session ended" — and, because the
 *     reloaded sign-in page probes `/auth/me` again, would do it forever.
 *   - `/auth/logout` — a 401 here means the session was already gone, which
 *     is a SUCCESSFUL sign-out arriving as an error status; `signOut.js`
 *     reads it that way and sends the user to `/` (#262's front door), not
 *     to /login. Two owners for one transition is one too many.
 *   - `/auth/register`, `/auth/forgot-password`, `/auth/reset-password` —
 *     public credential routes, reachable only from pages where there is no
 *     session to end. (Registration answers 400, not 401. The two
 *     password-reset routes were listed here pre-emptively by #409 while
 *     they still 404'd; #410 built them, and they answer 400 — never 401 —
 *     for a bad or expired token, partly BECAUSE this exemption's reasoning
 *     was already written down. The exemption stays regardless: if either
 *     ever emits a 401, it must not read as "your session ended".)
 *
 * Note what is NOT here: `PUT /auth/me` (the Settings display-name save) is a
 * genuine authenticated write, so its 401 genuinely means the session died
 * and must be acted on. The list is per-path, never per-namespace, for
 * exactly that reason.
 */
export const EXEMPT_API_PATHS = Object.freeze([
  "/auth/login",
  "/auth/register",
  "/auth/logout",
  "/auth/me",
  "/auth/change-password",
  "/auth/forgot-password",
  "/auth/reset-password",
]);

/**
 * `POST /auth/delete-account` (#1032) answers 401 for a proof that did not
 * confirm: `delete_wrong_password` and `delete_proof_invalid`. Both mean
 * "nothing has changed, and you're still signed in" and are shown on the
 * page (D57), so neither may read as an ended session. This is NOT a path
 * exemption: the route sits behind `requireUser`, whose own 401 (a session
 * that really died) carries no such code and must still end the session.
 */
export const DELETE_PROOF_REFUSAL_CODES = Object.freeze(["delete_wrong_password", "delete_proof_invalid"]);
const DELETE_ACCOUNT_API_PATH = "/auth/delete-account";

/**
 * Pages that need no session, so nothing on them can have lost one.
 *
 * This is the SECOND, independent guard, and it is here to make the loop
 * structurally impossible rather than merely enumerated-away: even if some
 * request from a public page 401s for a reason nobody predicted, and even if
 * `EXEMPT_API_PATHS` above misses it, the app cannot navigate from a public
 * page to the sign-in page claiming a session ended. `/login` being in the
 * set is the load-bearing entry — it is where this module sends people, so
 * it is the one place a mistake becomes a reload loop instead of a bug.
 *
 * Kept as literals rather than derived from `redirectTarget.js`'s
 * `PROTECTED_ROUTE_PATHS`: that list is an allowlist of destinations (its
 * failure mode is dropping a link), this one is a refusal to act (its failure
 * mode is a loop), and inverting one to get the other would make a route
 * added later default to the wrong side of both.
 */
export const PUBLIC_PATHS = Object.freeze(["/", "/login", "/register", "/forgot-password", "/reset-password", "/sign-in-link", "/sign-in-link/callback", "/privacy", "/terms", "/account-deleted"]);

function currentOrigin() {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin;
  }
  return "http://localhost";
}

/** Lowercased, trailing slashes stripped, `/` preserved. The same
 * normalisation `redirectTarget.js` applies, and for the same reason: the
 * router treats `/Login/` and `/login` as the same page, so a guard that did
 * not would be stricter than the thing it guards. */
function normalisePath(pathname) {
  return (pathname || "/").replace(/\/+$/, "").toLowerCase() || "/";
}

function basePathOf(apiBase) {
  try {
    return normalisePath(new URL(apiBase || "", currentOrigin()).pathname);
  } catch {
    return "/";
  }
}

/**
 * The failed request's path, relative to the API root — e.g. `/auth/login`
 * for a request axios sent to `https://cricket.example/api/auth/login`.
 *
 * Returns `null` when the request cannot be located at all. That case is
 * treated as "do not act": an unlocatable request is one whose exemption
 * cannot be checked, and the wrong way to fail there is to sign someone out.
 *
 * `apiBase` is threaded in from `api.js` rather than assumed to be `/api`,
 * because `REACT_APP_BACKEND_URL` can point the whole API at another origin
 * (or another path prefix) — stripping a hardcoded `/api` would then strip
 * nothing, and every path would miss the exemption list.
 */
export function requestApiPath(config, apiBase) {
  const url = typeof config?.url === "string" ? config.url : "";
  const baseURL = typeof config?.baseURL === "string" ? config.baseURL : "";
  // Axios ignores `baseURL` when the request URL is already absolute or
  // protocol-relative; mirroring that here keeps this reading the URL that
  // was actually sent rather than one this function invented.
  const isAbsolute = /^[a-z][a-z\d+\-.]*:/i.test(url) || url.startsWith("//");
  const joined = isAbsolute
    ? url
    : `${baseURL.replace(/\/+$/, "")}${url.startsWith("/") || url === "" ? url : `/${url}`}`;
  if (!joined) return null;

  let pathname;
  try {
    pathname = new URL(joined, currentOrigin()).pathname;
  } catch {
    return null;
  }

  const base = basePathOf(apiBase);
  const normalised = normalisePath(pathname);
  if (base !== "/" && (normalised === base || normalised.startsWith(`${base}/`))) {
    return normalisePath(normalised.slice(base.length));
  }
  return normalised;
}

/**
 * "This 401 means the session this tab is rendering no longer exists."
 *
 * Both halves are required: the status must be exactly 401 (never 403 — see
 * the header note on `PASSWORD_CHANGE_REQUIRED`), and the request must not be
 * one of the exempt paths.
 */
export function shouldEndSession(error, apiBase) {
  if (error?.response?.status !== 401) return false;
  const path = requestApiPath(error.config, apiBase);
  if (path === null) return false;
  if (path === DELETE_ACCOUNT_API_PATH && DELETE_PROOF_REFUSAL_CODES.includes(error.response?.data?.code)) return false;
  return !EXEMPT_API_PATHS.includes(path);
}

/**
 * One transition per page, ever.
 *
 * Module state, not a timer-based debounce. A dashboard mount fires half a
 * dozen reads at once, so a dead session produces a BURST of 401s within a
 * few milliseconds of each other; a naive handler would call
 * `location.assign` once per response. A time window would have to be tuned
 * against how long the burst lasts, and would still let a second transition
 * through for a request that lands after it. A latch needs neither: the first
 * 401 wins and every later one is a no-op, which is the actual requirement
 * ("one transition, not several") rather than an approximation of it.
 *
 * It is never reset in production, deliberately — the page is on its way to
 * unloading, and there is no state in which a second navigation to the same
 * URL would be useful. `resetSessionExpiryLatch` exists for tests only.
 */
let signInTransitionStarted = false;

/**
 * Takes the latch on behalf of a transition this module is NOT driving, and
 * returns a function that gives it back.
 *
 * This exists because of a conflict that only shows up against a real server,
 * and that the journey suite caught on the first run of this fix. `signOut.js`
 * sends a confirmed sign-out to `/` — #262's front door, chosen deliberately
 * because the 24h cookie made the landing page unreachable for anyone who had
 * ever signed in. But the instant `POST /auth/logout` succeeds, every request
 * this page still has in flight starts coming back 401: the dashboard alone
 * fires ten reads on mount, and a sign-out landing in that window produced
 * ELEVEN 401s in the captured trace. Without this, the first of them would
 * take the user to `/login?session=expired` instead — telling someone who
 * just pressed "Sign out" that their session ended unexpectedly, and quietly
 * undoing #262's fix in the process.
 *
 * Claimed BEFORE the logout request rather than after it, because the gap
 * being closed is exactly the one between the server deleting the row and the
 * client hearing about it. Released when the sign-out is reported as FAILED
 * (#326): that means the session is still alive, so a genuine expiry later in
 * the same page load must still be able to act.
 */
export function claimSessionTransition() {
  if (signInTransitionStarted) return () => {};
  signInTransitionStarted = true;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    signInTransitionStarted = false;
  };
}

/** Test-only. Nothing in `src/` outside the test suite may call this. */
export function resetSessionExpiryLatch() {
  signInTransitionStarted = false;
}

/** Test-only: has the latch been taken? */
export function sessionExpiryTransitionStarted() {
  return signInTransitionStarted;
}

/**
 * Leaves for the sign-in page. Returns whether it actually navigated.
 *
 * WHY A FULL PAGE LOAD, exactly as `signOut.js` argues for the outbound
 * case: a reload guarantees nothing from the dead session survives in memory
 * — no fetched roster, no half-rendered page, no context still holding the
 * last user. On the shared phone #262 is about, that is the whole point, and
 * here it is stronger still, because the session did not end at a moment the
 * user chose. It also side-steps the redirect race `signOut.js` documents:
 * there is no client-side `nav()` competing with `ProtectedRoute`'s own
 * `<Navigate>`, because the SPA stops existing.
 *
 * WHY NO RETURN DESTINATION. `?next=` / `location.state` would be the
 * friendlier thing, and `lib/redirectTarget.js` (#257) already exists to
 * validate one. It is deliberately not done here: the destination is a
 * hardcoded literal with nothing from the request, nothing from the current
 * URL, and nothing from any server response interpolated into it, so this
 * module contributes no open-redirect surface at all — not a validated one,
 * none. A session that ended without the user asking is a poor moment to
 * introduce a new attacker-influenced input to the navigation path, and the
 * cost is that sign-in lands on the dashboard instead of the page they were
 * on.
 */
export function endSessionAndSignIn() {
  if (signInTransitionStarted) return false;
  if (typeof window === "undefined" || !window.location) return false;
  // The public-page guard. Not latched: refusing on a public page is not a
  // transition, and latching it would make a later, legitimate 401 (after a
  // sign-in from that same page load) a no-op.
  if (PUBLIC_PATHS.includes(normalisePath(window.location.pathname))) return false;
  signInTransitionStarted = true;
  window.location.assign(SESSION_ENDED_DESTINATION);
  return true;
}

/** True when the sign-in page was reached by this module. Reads only the
 * fixed sentinel value — the parameter's content is never rendered, so
 * nothing an attacker can put in the query string reaches the page. */
export function sessionEndedRequested(search) {
  try {
    return new URLSearchParams(search || "").get(SESSION_ENDED_PARAM) === SESSION_ENDED_VALUE;
  } catch {
    return false;
  }
}

/**
 * Wires the above onto an axios instance. Called once, from `api.js`.
 *
 * The error is RE-REJECTED, never swallowed: every existing caller keeps the
 * rejection it already handles, so this is purely additive to their error
 * paths. Swallowing would silently convert every 401 into a hanging promise
 * in components that `await` — a second, worse version of the #336 spinner.
 */
export function installSessionExpiryInterceptor(instance, apiBase) {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (shouldEndSession(error, apiBase)) endSessionAndSignIn();
      return Promise.reject(error);
    },
  );
  return instance;
}
